import { Listbox } from "@headlessui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../../../stateManager/store";
import { data } from "../data";
import { ReactComponent as DropdownIcon } from "../../../../../../assets/svg/solobuilder/dropdown.svg";
import { pushFormActionStack, updateElement, updateElementOnUndoRedo } from "../../../../../../stateManager/store/formCreation_v2/actions";
import { getValue, getComponents } from "../../helpers";
import { FormCreationData } from "../../../../../../stateManager/store/formCreation_v2/types";
import { myRedoStack, myUndoStack } from "../../stack/Stack";

type Props = {
  label: string;
  isCheckedIn: boolean;
  defaultValue?: string
}

const excluded_types = [
  "Otp"
]

export const WizardWidth = (props: Props) => {
  const { components } = getComponents()
  const { selectedElementId, deviceType, isCoreV3, wrapperComputedStyles, computedStyles } = useSelector((state: AppState) => state.formJson)
  const { isCheckedIn, label } = props;
  const component: FormCreationData = components[selectedElementId];
  const dispatch = useDispatch()

  const widthOptions = data.wizardWidthMode ? data.wizardWidthMode.map((el) => ({ name: el.label })) : []
  const { type } = component;

  const onChangeMode = (key: string, value: any, name: string) => {
    // Store the current value as oldValue
    const oldValue = {
      wizardWidth: getValue(component, 'properties.styles.$deviceType.wizardWidth'.split('.'), deviceType) ?? calculateWidth(),
      wizardWidthMode: getValue(component, 'properties.styles.$deviceType.wizardWidthMode'.split('.'), deviceType)
    };

    if (key === 'properties.styles.$deviceType.wizardWidthMode') {
      myUndoStack.push({
        id: selectedElementId,
        path: key,
        value: oldValue,
        label: "",
        properties: {},
        type: "",
        fields: []
      });
      myRedoStack.clear();
    }

    dispatch(updateElement({
      id: selectedElementId,
      path: key,
      value
    }));

    let wizardWidthValue = "";
    if (value === "auto") {
      wizardWidthValue = "auto";
    } else if (value === "min-content") {
      wizardWidthValue = "min-content";
    } else if (value === "max-content") {
      wizardWidthValue = "max-content";
    } else {
      wizardWidthValue = value;
    }

    // Determine width mode based on value
    let wizardWidthMode = value;
    if (name === "%") {
      wizardWidthMode = "%";
    }
    if (name === "px") {
      wizardWidthMode = "px";
    }

    // Store the updated value as newValue
    const newValue = {
      wizardWidth: wizardWidthValue,
      wizardWidthMode: wizardWidthMode
    };

    dispatch(pushFormActionStack({
      undo: () => {
        const poppedElement = myUndoStack.pop();
        if (poppedElement) {

          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: 'properties.styles.$deviceType.wizardWidth',
            value: poppedElement.value.width || "",
            increment: -1
          }));
          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: 'properties.styles.$deviceType.wizardWidthMode',
            value: poppedElement.value.wizardWidthMode || "",
            increment: -1
          }));
          myRedoStack.push({ ...poppedElement, value: newValue });
        }
      },
      redo: () => {
        const poppedElement = myRedoStack.pop();
        if (poppedElement) {
          const oldValue = {
            wizardWidth: getValue(component, 'properties.styles.$deviceType.wizardWidth'.split('.'), deviceType) ?? calculateWidth(),
            wizardWidthMode: getValue(component, 'properties.styles.$deviceType.wizardWidthMode'.split('.'), deviceType)
          };
          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: 'properties.styles.$deviceType.wizardWidth',
            value: poppedElement.value.wizardWidth,
            increment: 1
          }));
          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: 'properties.styles.$deviceType.wizardWidthMode',
            value: poppedElement.value.wizardWidthMode,
            increment: 1
          }));
          myUndoStack.push({ ...poppedElement, value: oldValue });
        }
      }
    }));
  };

  const onChange = (key: string, value: any) => {
    const oldValue = getValue(component, key.split('.'), deviceType) ? getValue(component, key.split('.'), deviceType) : calculateWidth();
    myUndoStack.push({
      id: selectedElementId,
      path: key,
      value: oldValue,
      label: "",
      properties: {},
      type: "",
      fields: []
    })
    myRedoStack.clear();
    dispatch(updateElement({
      id: selectedElementId,
      path: key,
      value
    }))
    dispatch(pushFormActionStack({
      undo: () => {
        const poppedElement = myUndoStack.pop();
        if (poppedElement) {
          myRedoStack.push({ ...poppedElement, value })
          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: poppedElement.path,
            value: poppedElement.value || "",
            increment: -1
          }))
        }
      },
      redo: () => {
        const poppedElement = myRedoStack.pop();
        if (poppedElement) {
          myUndoStack.push({ ...poppedElement, value: oldValue });
          dispatch(updateElementOnUndoRedo({
            id: poppedElement.id,
            path: poppedElement.path,
            value: poppedElement.value,
            increment: 1
          }))
        }
      }
    }))
  }

  useEffect(() => {
    if (!(component.properties.styles[deviceType].wizardWidthMode)) {
      if (type === "Row" || type === "Grid") {
        dispatch(updateElement({ id: selectedElementId, path: `properties.styles.$deviceType.wizardWidthMode`, value: "%" }));
        dispatch(updateElement({ id: selectedElementId, path: `properties.styles.$deviceType.wizardWidth`, value: "100" }));
      }else{
        dispatch(updateElement({ id: selectedElementId, path: `properties.styles.$deviceType.wizardWidthMode`, value: "px" }));
        dispatch(updateElement({ id: selectedElementId, path: `properties.styles.$deviceType.wizardWidth`, value: component.properties.styles[deviceType].width }));
      }
    }
  }, [deviceType, computedStyles, dispatch, selectedElementId, component]);

  const layout_components = [
    "FormWizard"
  ]

  const calculateWidth = () => {
    const deviceTypeWidth = getValue(
      component,
      ["properties", "styles", deviceType, "wizardWidth"],
      deviceType
    );
    if (deviceTypeWidth === undefined) {
      let wrapperStyles: any;
      if (layout_components.includes(type)) {
        wrapperStyles = wrapperComputedStyles
      } else {
        wrapperStyles = computedStyles;
      }
      const wizardWidth = (wrapperStyles && wrapperStyles.wizardWidth && wrapperStyles.wizardWidth.match(/\d+/) ? wrapperStyles.wizardWidth.match(/\d+/)[0] : null);
      return wizardWidth
    }
    return deviceTypeWidth
  }

  const getWidthMode = () => {
    const deviceWidthMode = getValue(component, `properties.styles.$deviceType.wizardWidthMode`.split("."), deviceType)
    if (!deviceWidthMode) {
      const reg = /([a-z%]+)/
      const wrapperStyles = wrapperComputedStyles.hasOwnProperty("wizardWidth") ? wrapperComputedStyles : computedStyles;
      const wizardWidthMode = (wrapperStyles && wrapperStyles.wizardWidth && wrapperStyles.wizardWidth.match(reg) ? wrapperStyles.wizardWidth  .match(reg)[0] : null);
      return wizardWidthMode
    }
    return deviceWidthMode
  }

  if (isCoreV3 && excluded_types.includes(type)) {
    return null
  }

  return (
    <div className="panel-div">
      <label
        htmlFor="wizardWidth"
        className="panel-label"
      >
        {label}
      </label>
      <div
          style={{ cursor: !isCheckedIn ? "not-allowed" : "auto" }} 
          className="panel-input flex items-center"
      >
        <div className="relative w-1/2 bg-white text-gray-900" style={component.properties.styles[deviceType].wizardWidthMode === "%" || component.properties.styles[deviceType].wizardWidthMode === "px" ? { borderRight: "#B4B4B4 solid 1px" } : {width: "100%", paddingLeft: 0}}>
          <Listbox disabled={!isCheckedIn}
            value={{ name: data.wizardWidthMode.find(item => item.value === getWidthMode())?.label ?? data.wizardWidthMode[0].label }}
            onChange={(option) => {
              if (data.wizardWidthMode) {
                const item = data.wizardWidthMode.find(item => item.label === option.name)
                if (item) {
                  onChangeMode(`properties.styles.$deviceType.wizardWidthMode`, item.value, option.name)
                  if (option.name === "px") {
                    onChangeMode(`properties.styles.$deviceType.wizardWidth`, "100", option.name)
                  } else if (option.name === "%") {
                    onChangeMode(`properties.styles.$deviceType.wizardWidth`, deviceType === 'Lg' ? '20' : deviceType === 'Md' ? '30' : '100', option.name)
                  } else {
                    onChangeMode(`properties.styles.$deviceType.wizardWidth`, item.value, option.name)
                  }
                }
              }
            }}>
            <Listbox.Button style={{ cursor: !props.isCheckedIn ? "not-allowed" : "auto" }} className="relative w-full text-lx cursor-pointer rounded-sm bg-white item-center pl-4 h-full text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
              <span className="text-lg">{data.wizardWidthMode.find(item => item.value === getWidthMode())?.label ?? data.wizardWidthMode[0].label}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <DropdownIcon className="h-3 w-3 mt-1 text-gray-400" />
              </span>
            </Listbox.Button>
            <Listbox.Options className="top-10 absolute max-h-60 w-52 overflow-auto rounded-md bg-white py-3 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-lg z-40">
              {widthOptions.map((type, colorIdx) => (
                <Listbox.Option
                  key={colorIdx}
                  className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? "text-yellow-500" : "text-gray-900"}`}
                  value={type}
                >
                  {({ selected }) => (
                    <>
                      <span className={`block truncate ${selected ? "text-lx" : "text-lg"}`}>
                        {type.name}
                      </span>
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Listbox></div>
        <br />
        {component.properties.styles[deviceType].wizardWidthMode === "%" || component.properties.styles[deviceType].wizardWidthMode === "px" ?
          <input
            style={{ cursor: !isCheckedIn ? "not-allowed" : "auto" }}
            disabled={!isCheckedIn}
            type="text"
            id="width"
            className="col-span-2 bg-white text-gray-900 rounded-sm px-3 w-1/2 border-0 h-full outline-none"
            value={
              isCoreV3 ?
                calculateWidth()
                : getValue(component, `properties.styles.$deviceType.wizardWidth`.split("."), deviceType)
            }
            onChange={(e: any) => {
              let value = e.target.value
              const regex = /^(\s*|\d+)$/
              const isValid = regex.test(value)
              const item = component.properties.styles[deviceType].wizardWidthMode === "%"
              if (item && isValid && value > 100) {
                value = 100
              }
              isValid && onChange(`properties.styles.$deviceType.wizardWidth`, value)
            }}
          /> : <div></div>}
      </div>
    </div >
  )
};
